
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  isAdminUIEnabled: boolean; // 是否在页面上显示管理按钮
  login: (password: string) => boolean;
  logout: () => void;
  enableAdminUI: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getAdminPassword = () => {
  try {
    const envPassword = process.env.ADMIN_PASSWORD;
    if (typeof envPassword === 'string' && envPassword !== '' && envPassword !== 'undefined') {
      return envPassword;
    }
  } catch (e) {
    // Ignore
  }
  return "admin"; 
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('is_admin_authenticated') === 'true';
  });
  
  const [isAdminUIEnabled, setIsAdminUIEnabled] = useState(() => {
    const sessionUI = sessionStorage.getItem('admin_ui_active_v2') === 'true';
    const params = new URLSearchParams(window.location.search);
    const hasSecretParam = params.get('admin') === 'meitai_secret' || params.get('edit') === 'true';
    return sessionUI || hasSecretParam;
  });

  useEffect(() => {
    // 1. 从 Session 恢复状态
    const sessionAuth = sessionStorage.getItem('is_admin_authenticated') === 'true';
    const sessionUI = sessionStorage.getItem('admin_ui_active_v2') === 'true';
    
    // 2. 判定管理模式是否开启
    const params = new URLSearchParams(window.location.search);
    const hasSecretParam = params.get('admin') === 'meitai_secret' || params.get('edit') === 'true';

    // 只要满足其中一个条件，就开启管理UI
    if (sessionUI || hasSecretParam) {
      const timer = setTimeout(() => setIsAdminUIEnabled(true), 0);
      sessionStorage.setItem('admin_ui_active_v2', 'true');
      return () => clearTimeout(timer);
    }
  }, []);

  const login = (password: string): boolean => {
    const correctPassword = getAdminPassword();
    if (password.trim() === correctPassword.trim()) {
      setIsAuthenticated(true);
      setIsAdminUIEnabled(true);
      sessionStorage.setItem('is_admin_authenticated', 'true');
      sessionStorage.setItem('admin_ui_active_v2', 'true');
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    setIsAdminUIEnabled(false);
    sessionStorage.removeItem('is_admin_authenticated');
    sessionStorage.removeItem('admin_ui_active_v2');
    window.location.href = window.location.pathname; 
  };

  const enableAdminUI = () => {
    setIsAdminUIEnabled(true);
    sessionStorage.setItem('admin_ui_active_v2', 'true');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isAdminUIEnabled, login, logout, enableAdminUI }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
